import { ApiError } from "../api/api-client";
import { MiningSessionApi, miningResponseClock, sessionChallengeSchema,
  type MiningOwner, type MiningAllocationRequest } from "../api/mining-session";
import type { WalletSessionSnapshot } from "../wallet/wallet-session";
import type { MiningChallenge, MiningChallengeWire, MiningProof } from "./types";

export type MiningAllocationPolicy = Readonly<{ allocationSize: number }>;
export type ServerAllocation = MiningAllocationRequest & {
  sessionRevision: number; startNonce: string; endNonceExclusive: string;
};
type Authority = { challenge: MiningChallenge; owner: MiningOwner; revision: number;
  expiresAt: number; now(): number; cadence: number; abort: AbortController };
// One ID per document/module runtime, never persisted or cloned from sessionStorage.
let documentWorker: string | undefined;
function workerId(): string { return documentWorker ??= crypto.randomUUID(); }

export function sessionWire(challenge: MiningChallengeWire) {
  return sessionChallengeSchema.parse({ ...challenge, chainId: Number(challenge.chainId),
    difficultyEpochId: Number(challenge.difficultyEpochId), issuedAt: Number(challenge.issuedAt), expiresAt: Number(challenge.expiresAt) });
}

/** Server execution authority only; wallet metadata stays owned by WalletSessionController. */
export class ServerMiningLease {
  readonly workerId: string;
  private authority: Authority | null = null;
  private renewal: ReturnType<typeof setTimeout> | undefined;
  private expiry: ReturnType<typeof setTimeout> | undefined;
  private epoch = 0;
  private rangeLimit: number;
  private pendingRange: MiningAllocationRequest | null = null;
  private needsRevalidation = false;
  private revalidation: Promise<void> | null = null;
  private readonly channel: BroadcastChannel | null;
  private readonly onResume = () => { this.needsRevalidation = true; };
  constructor(readonly api: MiningSessionApi, private readonly auth: () => WalletSessionSnapshot,
    readonly allocationPolicy: MiningAllocationPolicy | null, private readonly lost: (reason: string) => void,
    id?: string) {
    this.workerId = id ?? workerId();
    this.rangeLimit = allocationPolicy?.allocationSize ?? 0;
    this.channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel("merklewood.mining-handover");
    if (this.channel) this.channel.onmessage = (event: MessageEvent<unknown>) => {
      const data = event.data;
      if (typeof data !== "object" || !data) return;
      const message = data as Record<string, unknown>;
      if (message.challengeId === this.authority?.challenge.challengeId && message.workerId !== this.workerId)
        this.revoke("MINING_MOVED"); // Advisory stop only; never grants execution permission.
    };
    if (allocationPolicy && (!Number.isSafeInteger(allocationPolicy.allocationSize) ||
      allocationPolicy.allocationSize < 1 || allocationPolicy.allocationSize > 100_000_000)) throw new Error("MINING_ALLOCATION_POLICY_INVALID");
    if (typeof window !== "undefined") {
      window.addEventListener("pageshow", this.onResume);
      window.addEventListener("online", this.onResume);
      document.addEventListener("visibilitychange", this.onResume);
    }
  }
  identity() {
    const auth = this.auth();
    if (!auth.authenticated || !auth.account || !auth.chainId) throw new Error("MINING_AUTH_REQUIRED");
    return { walletAddress: auth.account, chainId: auth.chainId };
  }
  current(signal?: AbortSignal) { return this.api.current(this.workerId, this.identity(), signal); }
  getChallenge(id: string, signal?: AbortSignal) { return this.api.getChallenge(id, this.identity(), signal); }
  create(token: string, signal?: AbortSignal) {
    this.requirePolicy();
    return this.api.create(token, this.identity(), signal);
  }
  requirePolicy(): void {
    if (!this.allocationPolicy) throw new Error("MINING_ALLOCATION_DISABLED");
  }
  assert(): Authority {
    const authority = this.authority;
    const auth = this.auth();
    if (!authority || authority.abort.signal.aborted || !auth.authenticated || auth.sessionRevision !== authority.revision ||
      auth.account?.toLowerCase() !== authority.challenge.walletAddress.toLowerCase() ||
      String(auth.chainId) !== authority.challenge.chainId || authority.now() >= authority.expiresAt * 1_000)
      throw new Error("MINING_LEASE_LOST");
    return authority;
  }
  async acquire(challenge: MiningChallenge, takeover: boolean, signal: AbortSignal): Promise<number> {
    this.requirePolicy();
    const epoch = ++this.epoch;
    const revision = this.auth().sessionRevision;
    this.identity();
    const started = { wallAt: Date.now(), monotonicAt: performance.now() };
    // No automatic acquire/takeover retry. An ambiguous reply needs a new explicit action.
    const lease = await this.api.acquire(challenge.challengeId, this.workerId, takeover, signal);
    signal.throwIfAborted();
    if (epoch !== this.epoch || revision !== this.auth().sessionRevision) throw new Error("MINING_AUTH_CHANGED");
    const now = miningResponseClock(lease.serverTime, started);
    const previous = this.authority;
    this.authority = { challenge, owner: { workerId: this.workerId, generation: lease.generation }, revision,
      expiresAt: Math.min(lease.leaseExpiresAt, Number(challenge.expiresAt), this.auth().sessionExpiresAt ?? 0),
      now, cadence: lease.heartbeatEverySeconds * 1_000, abort: new AbortController() };
    if (previous?.owner.generation !== lease.generation || previous.revision !== revision) this.pendingRange = null;
    this.needsRevalidation = false;
    this.assert();
    this.channel?.postMessage({ challengeId: challenge.challengeId, workerId: this.workerId });
    this.arm();
    return lease.generation;
  }
  private arm(): void {
    clearTimeout(this.renewal); clearTimeout(this.expiry);
    const authority = this.assert();
    this.expiry = setTimeout(() => this.revoke("MINING_LEASE_EXPIRED"),
      Math.max(0, authority.expiresAt * 1_000 - authority.now()));
    this.renewal = setTimeout(() => {
      Promise.resolve().then(() => this.validate()).catch(error => {
        // Definite errors revoke in validate(); transport uncertainty can only
        // use the last confirmed deadline, never a speculative extension.
        if (this.authority === authority && !authority.abort.signal.aborted && !(error instanceof ApiError && error.status < 500)) {
          try { this.arm(); } catch { this.revoke("MINING_LEASE_EXPIRED"); }
        }
      });
    }, authority.cadence);
  }
  private validate(): Promise<void> {
    if (this.revalidation) return this.revalidation;
    const authority = this.assert();
    const epoch = this.epoch;
    const started = { wallAt: Date.now(), monotonicAt: performance.now() };
    const pending = this.api.heartbeat(authority.challenge.challengeId, authority.owner, authority.abort.signal).then(lease => {
      this.assert();
      if (epoch !== this.epoch || this.authority !== authority) throw new Error("MINING_LEASE_LOST");
      authority.expiresAt = Math.min(lease.leaseExpiresAt, Number(authority.challenge.expiresAt), this.auth().sessionExpiresAt ?? 0);
      authority.now = miningResponseClock(lease.serverTime, started);
      this.needsRevalidation = false;
      this.arm();
    }).catch((error: unknown) => {
      if (this.authority === authority && error instanceof ApiError && (error.status < 500 || error.code === "INVALID_SERVER_RESPONSE")) this.revoke(error.code);
      throw error;
    }).finally(() => { if (this.revalidation === pending) this.revalidation = null; });
    this.revalidation = pending;
    return pending;
  }
  async beforeDispatch(): Promise<void> {
    try {
      this.assert();
      if (this.needsRevalidation) await this.validate();
      this.assert();
    } catch (error) {
      this.revoke("MINING_LEASE_LOST");
      throw new DOMException(error instanceof Error ? error.message : "Mining lease lost", "AbortError");
    }
  }
  async allocate(signal: AbortSignal, measuredRate: number | null): Promise<ServerAllocation> {
    const authority = this.assert();
    const size = measuredRate !== null && Number.isFinite(measuredRate) && measuredRate > 0 ?
      Math.min(this.rangeLimit, Math.max(1, Math.ceil(measuredRate * 5))) : Math.min(this.rangeLimit, 1_000_000);
    let request = this.pendingRange ??= { ...authority.owner, requestId: crypto.randomUUID(), size };
    let transportRetried = false;
    let sizeReductions = 0;
    const assertCurrent = () => {
      signal.throwIfAborted();
      if (this.assert() !== authority) throw new Error("MINING_LEASE_LOST");
    };
    for (;;) {
      assertCurrent();
      try {
        const range = await this.api.allocate(authority.challenge.challengeId, request, signal);
        assertCurrent();
        this.pendingRange = null;
        return { ...request, sessionRevision: authority.revision, startNonce: range.startNonce, endNonceExclusive: range.endNonceExclusive };
      } catch (error) {
        assertCurrent();
        // Only this definite rejection permits a smaller, new request. At most
        // eight reductions cover the full route ceiling down to one nonce.
        if (error instanceof ApiError && error.status === 400 && error.code === "MINING_RANGE_SIZE_INVALID") {
          this.pendingRange = null;
          if (request.size === 1 || sizeReductions >= 8) throw error;
          this.rangeLimit = Math.max(1, Math.floor(request.size / 10));
          request = this.pendingRange = { ...authority.owner, requestId: crypto.randomUUID(), size: this.rangeLimit };
          sizeReductions++;
          continue;
        }
        // A possibly accepted allocation gets one exact retry, never resizing
        // or changing its ID. Keep it pending if that retry is also uncertain.
        if (!transportRetried && (error instanceof TypeError || error instanceof ApiError &&
          error.status >= 500 && error.code !== "INVALID_SERVER_RESPONSE")) {
          transportRetried = true;
          continue;
        }
        throw error;
      }
    }
  }
  owner(): ServerAllocation["generation"] { return this.assert().owner.generation; }
  deadline() {
    const authority = this.assert();
    const remaining = Math.max(0, authority.expiresAt * 1_000 - authority.now());
    return { wallAt: Date.now() + remaining, monotonicAt: performance.timeOrigin + performance.now() + remaining };
  }
  matches(range: ServerAllocation): boolean {
    const authority = this.assert();
    return range.workerId === authority.owner.workerId && range.generation === authority.owner.generation &&
      range.sessionRevision === authority.revision;
  }
  async submit(challenge: MiningChallenge, nonce: string, provenance: MiningOwner, signal?: AbortSignal): Promise<MiningProof> {
    // Exact accepted-result recovery intentionally retains the original owner.
    // The BE may return an already accepted proof before validating its old lease.
    return this.api.submit(sessionWire(challenge), nonce,
      { workerId: provenance.workerId, generation: provenance.generation }, signal);
  }
  private revoke(reason: string): void { this.invalidate(); this.lost(reason); }
  invalidate(): void {
    this.epoch++;
    this.authority?.abort.abort();
    clearTimeout(this.renewal); clearTimeout(this.expiry);
    // Retain captured owner for best-effort release; assert() is fenced by abort.
  }
  async release(): Promise<void> {
    const old = this.authority;
    this.invalidate();
    this.authority = null;
    this.pendingRange = null;
    if (old) {
      const auth = this.auth();
      if (auth.authenticated && auth.sessionRevision === old.revision)
        await this.api.release(old.challenge.challengeId, old.owner).catch(() => {
          // Release is best effort; local work is already fenced and server TTL remains authoritative.
        });
    }
  }
  dispose(): void {
    this.invalidate();
    this.channel?.close();
    if (typeof window !== "undefined") {
      window.removeEventListener("pageshow", this.onResume);
      window.removeEventListener("online", this.onResume);
      document.removeEventListener("visibilitychange", this.onResume);
    }
  }
}
